import React from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Stack,
  Chip,
  Divider,
  IconButton,
  Badge,
  Fab,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import PublicIcon from "@mui/icons-material/Public";
import DataUsageIcon from "@mui/icons-material/DataUsage";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PhoneIcon from "@mui/icons-material/Phone";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import { useLocation, useNavigate } from "react-router-dom";
import { PackageModel } from "../repositories/package.repository";
import { useCartStore } from "../store/useCartStore";
import { useSnackbarStore } from "../store/useSnackbarStore";
import { mapPackageToCartItem } from "../utils/mappers/packageToCart";
import RoundFlag from "../components/common/RoundFlag";
import { useCurrencyStore } from "../store/useCurrencyStore";
import { formatPackagePrice } from "../utils/currency";

interface PackageDetail {
  pkg: PackageModel;
}

const PackageDetailPage = () => {
  const navigate = useNavigate();
  const { pkg } = useLocation().state as PackageDetail;
  console.log(pkg);
  const showSnackbar = useSnackbarStore((s) => s.showSnackbar);
  const addToCart = useCartStore((s) => s.addToCart);
  const totalQty = useCartStore((s) => s.getTotalQuantity());
  const getQuantityById = useCartStore((s) => s.getQuantityById(pkg.packageId));
  const currency = useCurrencyStore((s) => s.currency);

  const handleAddToCart = () => {
    if (totalQty >= 5) {
      showSnackbar("You can't add more than 5 items", "warning");
      return;
    }
    addToCart([mapPackageToCartItem(pkg)]);
    showSnackbar("Added to cart", "success");
  };

  if (!pkg) return null;

  return (
    <Container maxWidth="md" sx={{ py: 2, pb: 8 }}>
      {" "}
      {/* extra bottom padding for FAB */}
      {/* Top Bar */}
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <IconButton size="large" onClick={() => navigate(-1)}>
          <ArrowBackIosNewIcon fontSize="small" />
        </IconButton>
        <Typography variant="body2" color="text.secondary">
          Back
        </Typography>
      </Stack>
      {/* Header */}
      <Stack spacing={0.5} mb={2}>
        <Typography variant="overline" color="text.secondary">
          eSIM Package
        </Typography>
        <Typography variant="h6" fontWeight={600}>
          {pkg.packageName}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {pkg.area}
        </Typography>
      </Stack>
      {/* Package Card */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          {/* Price */}
          <Stack spacing={0.25} mb={2}>
            <Stack
              direction={"row"}
              alignItems="center"
              justifyContent={"space-between"}
              width={"100%"}
              spacing={1}
            >
              <Typography sx={{ fontSize: 20 }} fontWeight={600}>
                {formatPackagePrice(pkg, currency)}
              </Typography>
              <Fab
                sx={{ boxShadow: "none" }}
                color="primary"
                variant="extended"
                size="small"
                aria-label="add to cart"
                onClick={handleAddToCart}
              >
                <Badge
                  badgeContent={getQuantityById || 0}
                  color="warning"
                  sx={{ mr: 1 }}
                >
                  <AddShoppingCartIcon />
                </Badge>
                Add to Cart
              </Fab>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              One-time payment · No contract
            </Typography>
          </Stack>

          <Divider sx={{ my: 2 }} />

          {/* Specs */}
          <Stack spacing={1.5}>
            <Spec icon={<DataUsageIcon />} label="Data" value={pkg.data} />
            <Spec icon={<PhoneIcon />} label="Minutes" value={pkg.minutes} />
            <Spec
              icon={<ScheduleIcon />}
              label="Validity"
              value={pkg.duration}
            />
            <Spec icon={<PublicIcon />} label="Coverage" value={pkg.area} />
          </Stack>

          {/* Supported Countries */}
          {pkg.countries.length > 0 && (
            <Box mt={3}>
              <Typography variant="subtitle2" fontWeight={600} mb={1}>
                {pkg.countries.length > 1
                  ? "Supported Countries"
                  : "Supported Country"}
              </Typography>

              <Stack direction="row" flexWrap="wrap" gap={1}>
                {pkg.countries.map((c) => (
                  <Chip
                    key={c.countryId}
                    label={c.countryName}
                    avatar={
                      <RoundFlag src={c.flagUrl} alt={c.countryName} size={22} />
                    }
                    sx={{
                      borderRadius: 2,
                      fontSize: 13,
                      bgcolor: "background.paper",
                      border: "none",
                      py: 2,
                      px: 1,
                      boxShadow: "0 2px 5px rgba(0,0,0,0.08)",
                      textTransform: "none",
                      fontWeight: 500,
                      transition: "transform 0.2s, box-shadow 0.2s",
                      "&:hover": {
                        transform: "translateY(-1px)",
                        boxShadow: "0 4px 10px rgba(0,0,0,0.12)",
                        bgcolor: "background.paper",
                      },
                    }}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {/* Operators by Country */}
          {pkg.countries.length > 0 && (
            <Box mt={3}>
              <Typography variant="subtitle2" fontWeight={600} mb={1.5}>
                Network Operators
              </Typography>

              <Stack spacing={1.5}>
                {pkg.countries.map((c) => (
                  <Box
                    key={c.countryId}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: "grey.50",
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    {/* Country Header */}
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      mb={1}
                    >
                      <RoundFlag
                        src={c.flagUrl}
                        alt={c.countryName}
                        size={20}
                      />
                      <Typography variant="body2" fontWeight={600}>
                        {c.countryName}
                      </Typography>
                    </Stack>

                    {/* Operators */}
                    {c.operators.length > 0 ? (
                      <Stack direction="row" flexWrap="wrap" gap={0.75}>
                        {c.operators.map((op) => (
                          <Chip
                            key={op.operatorId}
                            label={op.operatorName}
                            size="small"
                            sx={{
                              fontSize: 12,
                              borderRadius: 1,
                            }}
                          />
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Automatically connects to best available network
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default PackageDetailPage;

/* --- Helper --- */
const Spec = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) => (
  <Stack direction="row" spacing={1.5} alignItems="center">
    <Box color="text.secondary">{icon}</Box>
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500}>
        {value}
      </Typography>
    </Box>
  </Stack>
);
